import { resolveApiBaseUrl } from "./apiBase";

const API_BASE_URL = resolveApiBaseUrl();

export type Client404Kind =
  | 'unmatched_route'
  | 'collection_not_found'
  | 'component_not_found';

export type Client404Stats = {
  totalHits: number;
  byPath: { path: string; count: number }[];
  lastHit: {
    path: string;
    kind: Client404Kind;
    at: string;
    referrer: string | null;
  } | null;
};

export type Client404PathBreakdown = {
  path: string;
  totalHits: number;
  publicHits: number;
  authenticatedHits: number;
  byUser: {
    viewerUid: string;
    viewerEmail: string | null;
    hitCount: number;
    lastAt: string;
  }[];
};

export function logClient404Hit(payload: {
  path: string;
  kind: Client404Kind;
  referrer?: string | null;
}): void {
  const pathToSend =
    typeof payload.path === 'string' && payload.path.startsWith('/')
      ? payload.path
      : `/${typeof payload.path === 'string' ? payload.path : ''}`;
  const body = {
    path: pathToSend.slice(0, 2048),
    kind: payload.kind,
    referrer: payload.referrer ? String(payload.referrer).slice(0, 2048) : undefined,
  };
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  fetch(`${API_BASE_URL}/public/client-404/hit`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(body),
  }).catch(() => { });
}

export async function fetchClient404Stats(): Promise<Client404Stats> {
  const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
  if (!token) {
    throw new Error('Not authenticated');
  }
  const url = `${API_BASE_URL}/auth/admin/client-404-stats`;
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { message?: string }).message || `HTTP ${response.status}`);
  }
  const json = (await response.json()) as { success?: boolean; data?: Client404Stats };
  if (!json.data) {
    throw new Error('Invalid stats response');
  }
  return json.data;
}

export async function clearClient404Log(): Promise<{ deletedCount: number }> {
  const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
  if (!token) {
    throw new Error('Not authenticated');
  }
  const url = `${API_BASE_URL}/auth/admin/client-404-clear`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { message?: string }).message || `HTTP ${response.status}`);
  }
  const json = (await response.json()) as { success?: boolean; deletedCount?: number };
  if (!json.success) {
    throw new Error('Clear log failed');
  }
  return { deletedCount: json.deletedCount ?? 0 };
}

export async function fetchClient404PathBreakdown(path: string): Promise<Client404PathBreakdown> {
  const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
  if (!token) {
    throw new Error('Not authenticated');
  }
  const q = encodeURIComponent(path);
  const url = `${API_BASE_URL}/auth/admin/client-404-path?path=${q}`;
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { message?: string }).message || `HTTP ${response.status}`);
  }
  const json = (await response.json()) as { success?: boolean; data?: Client404PathBreakdown };
  if (!json.data) {
    throw new Error('Invalid breakdown response');
  }
  return json.data;
}
